<script setup lang="ts">
/**
 * One tile of an arrangement of panes: a head with name, caption and actions,
 * the content below it, and while another tile is being moved the five places
 * it can land. The owner lays tiles out and moves them; this only draws one.
 */
export type NxTileDrop = 'left' | 'right' | 'top' | 'bottom' | 'swap';

defineProps<{
  /** Name in the head, e.g. the app. */
  name: string;
  /** Caption after the name, e.g. the open document. */
  caption?: string;
  active?: boolean;
  /** This tile is the one being moved. */
  lifted?: boolean;
  /** The drop zones' texts while another tile is being moved; none otherwise. */
  dropLabels?: Record<NxTileDrop, string> | null;
  /** The zone under the pointer. */
  dropTarget?: NxTileDrop | null;
  /** Alone on its surface: no head and no frame, the content as a page. */
  plain?: boolean;
}>();

defineEmits<{
  /** A press on the head outside its actions: the owner may start moving the tile. */
  'head-pointerdown': [event: PointerEvent];
  /** Right-click (or the context-menu key) on the head. */
  'head-menu': [event: MouseEvent];
}>();

const zones: readonly NxTileDrop[] = ['left', 'top', 'swap', 'bottom', 'right'];
</script>

<template>
  <section
    class="nx-tile-pane"
    :class="{ 'is-active': active && !plain, 'is-lifted': lifted, 'is-plain': plain }"
  >
    <header
      v-if="!plain"
      class="nx-tile-pane-head"
      @pointerdown="$emit('head-pointerdown', $event)"
      @contextmenu.prevent="$emit('head-menu', $event)"
    >
      <slot name="icon" />
      <span class="nx-tile-pane-name">{{ name }}</span>
      <span class="nx-tile-pane-caption">{{ caption }}</span>
      <div class="nx-tile-pane-actions" @pointerdown.stop>
        <slot name="actions" />
      </div>
    </header>
    <div class="nx-tile-pane-body">
      <slot />
      <div v-if="dropLabels && !lifted" class="nx-tile-pane-drop">
        <span
          v-for="zone in zones"
          :key="zone"
          class="nx-tile-pane-zone"
          :class="[`is-${zone}`, { 'is-on': dropTarget === zone }]"
        >
          {{ dropLabels[zone] }}
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.nx-tile-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  overflow: hidden;
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
}
.nx-tile-pane.is-plain {
  border: 0;
  border-radius: 0;
}
.nx-tile-pane.is-active {
  border-color: var(--nxt-brand-bg);
  box-shadow: inset 0 2px 0 var(--nxt-brand-bg);
}
.nx-tile-pane.is-lifted {
  opacity: 0.6;
}
.nx-tile-pane-head {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs) var(--nxt-space-2xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  cursor: grab;
  user-select: none;
  touch-action: none;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-tile-pane-name {
  flex: none;
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-fg);
}
.nx-tile-pane-caption {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-tile-pane-actions {
  display: flex;
  flex: none;
  cursor: default;
}
.nx-tile-pane-body {
  position: relative;
  flex: 1;
  min-height: 0;
}
.nx-tile-pane-drop {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template: 1fr 1.4fr 1fr / 1fr 1.4fr 1fr;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-xs);
  background: color-mix(in srgb, var(--nxt-surface-bg) 60%, transparent);
}
.nx-tile-pane-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg);
  text-align: center;
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) dashed var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-md);
}
.nx-tile-pane-zone.is-left {
  grid-area: 2 / 1;
}
.nx-tile-pane-zone.is-top {
  grid-area: 1 / 2;
}
.nx-tile-pane-zone.is-swap {
  grid-area: 2 / 2;
}
.nx-tile-pane-zone.is-bottom {
  grid-area: 3 / 2;
}
.nx-tile-pane-zone.is-right {
  grid-area: 2 / 3;
}
.nx-tile-pane-zone.is-on {
  color: var(--nxt-brand-fg);
  background: var(--nxt-brand-bg);
  border-style: solid;
}
</style>
